import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { TrendingUp, ArrowRight, Lock } from 'lucide-react';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoginHero } from './LoginHero';

/**
 * Login page — split layout with the animated LoginHero (bouncing charts on
 * dark green) on the left and a focused form on the right. Deliberately no
 * rupee values, no portfolio totals, no P&L figures shown to logged-out
 * visitors (consistent with Landing).
 */
export const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    if (result.success) {
      navigate('/dashboard');
    } else if (result.storageBlock) {
      // iter-217 — route to a public overage-pay page (no auth required).
      navigate(`/storage-overage-due?email=${encodeURIComponent(result.email || email)}`);
    } else if (result.emailNotVerified) {
      // iter-233 — unverified signup; route to OTP entry.
      navigate('/verify-email', { state: { email: result.email || email } });
    } else if (result.subscriptionPaused) {
      // iter-234e — admin paused the user; show the paused message
      // inline on the login page (no app navigation).
      setError(
        (result.error || 'Your access has been paused.') +
        (result.pauseReason ? '' : ''),
      );
    } else {
      setError(result.error);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]" style={{ backgroundColor: '#FAFAF7' }}>
      {/* Left — animated bouncing-charts aside (lg+) */}
      <aside
        className="hidden lg:block relative overflow-hidden"
        data-testid="login-hero-panel"
      >
        <LoginHero />
      </aside>

      {/* Right — login card */}
      <main className="flex items-center justify-center p-6 lg:p-12">
        <div
          className="w-full max-w-md rounded-2xl p-8 lg:p-10"
          style={{ backgroundColor: '#FFFFFF', border: '1px solid #E5E5DF' }}
          data-testid="login-card"
        >
          <Link to="/" className="inline-flex items-center mb-8 group">
            <Logo size="lg" testid="login-logo" className="transition-transform group-hover:scale-105" />
          </Link>

          <span
            className="block text-[11px] font-semibold uppercase tracking-[0.2em] mb-2"
            style={{ color: '#1B4332' }}
          >
            Sign in
          </span>
          <h1
            className="text-2xl md:text-[26px] font-medium tracking-tight mb-2"
            style={{ color: '#1C1917', fontFamily: 'Work Sans', letterSpacing: '-0.01em' }}
            data-testid="login-brand-title"
          >
            Welcome back.
          </h1>
          <p className="text-sm mb-7" style={{ color: '#57534E' }}>
            Pick up where you left off — portfolios, transactions and tax reports.
          </p>

          {error && (
            <div
              className="mb-5 p-3 rounded-lg text-xs"
              style={{ backgroundColor: '#FCECE8', color: '#9A3412', border: '1px solid #F3D2C2' }}
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-xs font-medium" style={{ color: '#57534E' }}>
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                placeholder="you@example.com"
                data-testid="login-email-input"
                className="mt-1.5 h-11"
                style={{ borderColor: '#E5E5DF' }}
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-medium" style={{ color: '#57534E' }}>
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  className="text-[11px] font-medium hover:underline"
                  style={{ color: '#1B4332' }}
                  data-testid="forgot-password-link"
                >
                  Forgot?
                </Link>
              </div>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                className="mt-1.5 h-11"
                style={{ borderColor: '#E5E5DF' }}
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 flex items-center justify-center gap-2 transition-all duration-200 hover:shadow-[0_6px_18px_rgba(27,67,50,0.18)]"
              style={{ backgroundColor: '#1B4332', color: '#FFFFFF' }}
            >
              {loading ? 'Signing in…' : (
                <>
                  Sign in
                  <ArrowRight className="h-4 w-4" strokeWidth={2} />
                </>
              )}
            </Button>
          </form>

          <p className="mt-7 text-center text-xs" style={{ color: '#57534E' }}>
            Don't have an account?{' '}
            <Link
              to="/register"
              className="font-medium hover:underline"
              style={{ color: '#1B4332' }}
              data-testid="go-to-register"
            >
              Start 7-day free trial
            </Link>
          </p>

          {/* Honest, compact trust note — appears on every sign-in so returning
              users are reminded each visit that we're not cavalier with their
              financial data. Wording deliberately mirrors the Landing page's
              security section so the story stays consistent. */}
          <div
            className="mt-6 p-3.5 rounded-lg flex items-start gap-2.5"
            style={{ backgroundColor: '#F8F7F2', border: '1px solid #EAEAE2' }}
            data-testid="login-trust-note"
          >
            <Lock className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: '#1B4332', strokeWidth: 1.8 }} />
            <p className="text-[12px] leading-relaxed" style={{ color: '#57534E' }}>
              Your data is encrypted in transit (TLS) and at rest (AES-256), passwords
              are one-way bcrypt-hashed, and we never sell, share or browse your
              portfolios.{' '}
              <Link
                to="/"
                className="underline font-medium"
                style={{ color: '#1B4332' }}
              >
                Read our data-handling note
              </Link>
              .
            </p>
          </div>
        </div>
      </main>
    </div>
  );
};
